import { useCallback, useEffect, useMemo, useState } from 'react';
import { agentClasses, fetchAgents } from '../api/agents';
import { fetchAuditEvents, type AuditEvent } from '../api/audit';
import {
  fetchSessionHistory,
  fetchSessions,
  type SessionSummary,
  type SessionTurn,
} from '../api/sessions';
import './ActivityPage.css';

/**
 * What the runtime did — conversations, and the events around them.
 *
 * Ported from the predecessor's `Activity`, with one deliberate change.
 *
 * **The audit stream gets its own pane.** The predecessor only ever asked
 * for `?session_id=<id>`, so an event belonging to no session was
 * invisible. Core writes `session_id: ""` for exactly those —
 * `instance.spawned` is the common one — so on an instance whose agents
 * have not been spoken to yet, the old page was correct and entirely
 * blank. `wingd` after its cutover had zero sessions and eighteen events,
 * which is the case that made this worth changing rather than porting.
 *
 * **A session's detail is still the merged timeline**, because that is
 * the thing worth having: turns and audit events interleaved by
 * timestamp, so a tool call appears between the message that asked for it
 * and the answer that used it.
 */

interface TimelineEntry {
  kind: 'turn' | 'audit';
  timestamp_ms: number;
  turn?: SessionTurn;
  audit?: AuditEvent;
}

const EVENT_WINDOW = 200;

function dayLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function clockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function relative(ms: number | null): string {
  if (ms === null) return '';
  const sec = Math.floor((Date.now() - ms) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** A channel id reduced to its family, keeping anything unrecognised. */
function channelFamily(id: string | null | undefined): string {
  if (!id) return 'unknown';
  if (id === 'scheduled' || id === 'cli') return id;
  for (const prefix of ['telegram', 'slack', 'web']) {
    if (id.startsWith(prefix)) return prefix;
  }
  return id;
}

function renderDetail(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'string') {
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }
  return JSON.stringify(raw, null, 2);
}

/** Broad families, so an unknown event type still lands somewhere. */
function eventFamily(eventType: string): string {
  if (eventType.startsWith('tool.')) return 'tool';
  if (eventType.startsWith('context.')) return 'context';
  if (eventType.startsWith('interaction.')) return 'interaction';
  if (eventType.startsWith('instance.')) return 'instance';
  if (eventType.includes('deny') || eventType.includes('error')) return 'deny';
  return 'other';
}

export function ActivityPage() {
  const [pane, setPane] = useState<'sessions' | 'events'>('sessions');
  const [classes, setClasses] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState('');
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[] | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Non-critical: the filter is a convenience and its absence must not
    // stop the page from listing anything.
    fetchAgents()
      .then((rows) => setClasses(agentClasses(rows)))
      .catch(() => setClasses([]));
  }, []);

  useEffect(() => {
    let live = true;
    fetchSessions()
      .then((rows) => {
        if (!live) return;
        setSessions(rows);
        setError(null);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, []);

  const loadEvents = useCallback(() => {
    fetchAuditEvents({
      limit: EVENT_WINDOW,
      class_id: classFilter || undefined,
    })
      .then(setEvents)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  }, [classFilter]);

  useEffect(() => {
    if (pane === 'events') loadEvents();
  }, [pane, loadEvents]);

  const visibleSessions = useMemo(() => {
    const rows = sessions ?? [];
    return classFilter ? rows.filter((s) => s.class_id === classFilter) : rows;
  }, [sessions, classFilter]);

  const byDay = useMemo(() => {
    const groups = new Map<string, SessionSummary[]>();
    for (const s of visibleSessions) {
      const key = dayLabel(s.created_ms);
      const bucket = groups.get(key) ?? [];
      bucket.push(s);
      groups.set(key, bucket);
    }
    return [...groups.entries()];
  }, [visibleSessions]);

  async function openSession(sessionId: string) {
    setSelected(sessionId);
    setTimeline(null);
    setExpanded(null);
    // `allSettled`, not `all`: a session whose audit rows were pruned
    // still has its turns, and showing the turns beats showing an error.
    const [turns, auditRows] = await Promise.allSettled([
      fetchSessionHistory(sessionId),
      fetchAuditEvents({ session_id: sessionId, limit: 500 }),
    ]);
    if (turns.status === 'rejected' && auditRows.status === 'rejected') {
      setError(String(turns.reason));
      setTimeline([]);
      return;
    }
    const entries: TimelineEntry[] = [];
    if (turns.status === 'fulfilled') {
      for (const t of turns.value) {
        entries.push({ kind: 'turn', timestamp_ms: t.timestamp_ms, turn: t });
      }
    }
    if (auditRows.status === 'fulfilled') {
      for (const e of auditRows.value) {
        entries.push({ kind: 'audit', timestamp_ms: e.timestamp_ms, audit: e });
      }
    }
    entries.sort((a, b) => a.timestamp_ms - b.timestamp_ms);
    setTimeline(entries);
  }

  return (
    <div className="activity-page">
      <header className="activity-page__header">
        <div>
          <h1 className="activity-page__title">Activity</h1>
          <p className="activity-page__note">
            Conversations, and the events around them.
          </p>
        </div>
        <div className="activity-controls">
          <div className="activity-panes" role="group" aria-label="View">
            {(['sessions', 'events'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={`activity-pane-btn${pane === p ? ' is-active' : ''}`}
                onClick={() => setPane(p)}
              >
                {p}
              </button>
            ))}
          </div>
          {classes.length > 1 && (
            <label className="activity-filter">
              <span className="activity-filter__label">agent</span>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="">any</option>
                {classes.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>

      {error && <div className="activity-error">{error}</div>}

      {pane === 'events' ? (
        <EventStream events={events} window={EVENT_WINDOW} />
      ) : (
        <div className="activity-split">
          <aside className="activity-list" aria-label="Sessions">
            {sessions === null ? (
              <p className="activity-dim">Loading…</p>
            ) : visibleSessions.length === 0 ? (
              <div className="activity-empty">
                <p>No sessions yet.</p>
                <p className="activity-dim">
                  A session is created when someone talks to an agent
                  through a channel. Until then the <strong>events</strong>{' '}
                  pane is where this instance's activity is.
                </p>
              </div>
            ) : (
              byDay.map(([day, rows]) => (
                <div className="activity-day" key={day}>
                  <div className="activity-day__label">{day}</div>
                  <ul className="activity-day__items">
                    {rows.map((s) => (
                      <li key={s.session_id}>
                        <button
                          type="button"
                          className={`activity-session${
                            s.session_id === selected ? ' is-active' : ''
                          }`}
                          onClick={() => void openSession(s.session_id)}
                        >
                          <span className="activity-session__top">
                            <span
                              className={`activity-chan activity-chan--${channelFamily(s.channel_id)}`}
                            >
                              {channelFamily(s.channel_id)}
                            </span>
                            <span className="activity-session__class">
                              {s.class_id}
                            </span>
                          </span>
                          <span className="activity-session__meta activity-dim">
                            {s.turn_count} turn
                            {s.turn_count === 1 ? '' : 's'} ·{' '}
                            {relative(s.last_active_ms)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </aside>

          <section className="activity-detail">
            {!selected ? (
              <p className="activity-dim">Nothing selected.</p>
            ) : timeline === null ? (
              <p className="activity-dim">Loading…</p>
            ) : timeline.length === 0 ? (
              <p className="activity-dim">
                This session has no turns and no events recorded.
              </p>
            ) : (
              <ol className="activity-timeline">
                {timeline.map((entry, i) =>
                  entry.kind === 'turn' && entry.turn ? (
                    <li className="activity-turn" key={`t${entry.turn.turn_id}`}>
                      <div className="activity-turn__head">
                        <span
                          className={`activity-role activity-role--${entry.turn.role}`}
                        >
                          {entry.turn.role}
                        </span>
                        <span className="activity-dim">
                          {clockTime(entry.timestamp_ms)}
                          {entry.turn.token_count !== null &&
                            ` · ${entry.turn.token_count} tok`}
                          {entry.turn.compacted ? ' · compacted' : ''}
                        </span>
                      </div>
                      <pre className="activity-turn__body">
                        {entry.turn.content}
                      </pre>
                      {entry.turn.tool_calls &&
                        entry.turn.tool_calls.length > 0 && (
                          <ul className="activity-tools">
                            {entry.turn.tool_calls.map((tc, j) => (
                              <li key={tc.id ?? j}>
                                <code>{tc.name ?? '(unnamed)'}</code>
                                {tc.arguments && (
                                  <span className="activity-dim">
                                    {' '}
                                    {JSON.stringify(tc.arguments).slice(0, 160)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                    </li>
                  ) : entry.audit ? (
                    <li
                      className={`activity-event activity-event--${eventFamily(entry.audit.event_type)}`}
                      key={`a${entry.audit.id}-${i}`}
                    >
                      <button
                        type="button"
                        className="activity-event__head"
                        onClick={() =>
                          setExpanded(
                            expanded === entry.audit!.id
                              ? null
                              : entry.audit!.id,
                          )
                        }
                      >
                        <span className="activity-event__type">
                          {entry.audit.event_type}
                        </span>
                        <span className="activity-event__summary">
                          {entry.audit.summary}
                        </span>
                        <span className="activity-dim">
                          {clockTime(entry.timestamp_ms)}
                        </span>
                      </button>
                      {expanded === entry.audit.id && entry.audit.detail && (
                        <pre className="activity-event__detail">
                          {renderDetail(entry.audit.detail)}
                        </pre>
                      )}
                    </li>
                  ) : null,
                )}
              </ol>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function EventStream({
  events,
  window: windowSize,
}: {
  events: AuditEvent[] | null;
  window: number;
}) {
  const [expanded, setExpanded] = useState<number | null>(null);
  if (events === null) return <p className="activity-dim">Loading…</p>;
  if (events.length === 0) {
    return (
      <p className="activity-dim">
        No audit events. The runtime records one when an instance spawns, so
        an empty stream means nothing has started.
      </p>
    );
  }
  return (
    <>
      <p className="activity-dim">
        {events.length} of the last {windowSize}, newest first
      </p>
      <ul className="activity-stream">
        {events.map((e) => (
          <li
            key={e.id}
            className={`activity-event activity-event--${eventFamily(e.event_type)}`}
          >
            <button
              type="button"
              className="activity-event__head"
              onClick={() => setExpanded(expanded === e.id ? null : e.id)}
            >
              <span className="activity-event__type">{e.event_type}</span>
              <span className="activity-event__summary">{e.summary}</span>
              <span className="activity-event__who activity-dim">
                {e.instance_id}
              </span>
              <span className="activity-dim">{relative(e.timestamp_ms)}</span>
            </button>
            {expanded === e.id && (
              <pre className="activity-event__detail">
                {renderDetail(e.detail) || '(no detail)'}
                {e.session_id
                  ? `\n\nsession: ${e.session_id}`
                  : '\n\nno session'}
                {e.trace_id ? `\ntrace: ${e.trace_id}` : ''}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
